import TranslateIcon from '@mui/icons-material/Translate';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { BOOK_LANGUAGE_FORM_EN_ID } from '../../Settings/lists/bookLanguageFormSupport';
import type { BookLanguageRow } from '../../Settings/lists/singleTableListSupport';
import type { BookFormDraft } from './types';

export type BookFormStepTitleProps = {
  draft: BookFormDraft;
  enabledLanguages: BookLanguageRow[];
  hasEnEnabled: boolean;
  hasOtherEnabledLangForTitleTranslate: boolean;
  showTitleTranslateFromEn: boolean;
  canTranslateTitleFromEn: boolean;
  titleTranslating: boolean;
  titleTranslateError: string | null;
  onDismissTitleTranslateError: () => void;
  onTranslateTitleFromEn: () => void;
  setTitleForLang: (langId: string, value: string) => void;
  stepNavigation: React.ReactNode;
};

export default function BookFormStepTitle({
  draft,
  enabledLanguages,
  hasEnEnabled,
  hasOtherEnabledLangForTitleTranslate,
  showTitleTranslateFromEn,
  canTranslateTitleFromEn,
  titleTranslating,
  titleTranslateError,
  onDismissTitleTranslateError,
  onTranslateTitleFromEn,
  setTitleForLang,
  stepNavigation,
}: BookFormStepTitleProps) {
  return (
    <Box>
      {enabledLanguages.length === 0 ? (
        <Alert severity="info">Эхний алхамд дор хаяж нэг хэл идэвхжүүлнэ үү.</Alert>
      ) : (
        <>
          <Typography
            variant="h5"
            component="h2"
            sx={(theme) => ({ fontWeight: 600, color: theme.palette.text.primary, mb: 1 })}
          >
            Гарчиг (сонгосон хэл бүрээр)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 'md' }}>
            Идэвхтэй хэл бүрээр гарчиг оруулна. Англи (en) идэвхтэй бол en гарчиг заавал.
          </Typography>

          {showTitleTranslateFromEn ? (
            <Box sx={{ mt: 1, mb: 2 }}>
              <Button
                type="button"
                variant="outlined"
                size="small"
                startIcon={
                  titleTranslating ? <CircularProgress size={16} color="inherit" /> : <TranslateIcon />
                }
                disabled={!canTranslateTitleFromEn || titleTranslating}
                onClick={() => void onTranslateTitleFromEn()}
              >
                Англиас бусад хэл рүү орчуулах (гарчиг)
              </Button>
            </Box>
          ) : hasEnEnabled && !hasOtherEnabledLangForTitleTranslate ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, mb: 2 }}>
              Олон хэл рүү орчуулахын тулд «Хэл» алхамд англиас өөр дор хаяж нэг хэлийг идэвхжүүлнэ үү.
            </Typography>
          ) : null}
          {titleTranslateError ? (
            <Alert severity="error" onClose={onDismissTitleTranslateError} sx={{ mt: 1, mb: 2 }}>
              {titleTranslateError}
            </Alert>
          ) : null}

          {enabledLanguages.map((lang) => {
            const isEn = lang.id === BOOK_LANGUAGE_FORM_EN_ID;
            const val = draft.name_map[lang.id] ?? '';
            return (
              <TextField
                key={lang.id}
                label={`${lang.name} (${lang.id})`}
                value={val}
                onChange={(e) => setTitleForLang(lang.id, e.target.value)}
                required={isEn}
                fullWidth
                autoComplete="off"
                margin="dense"
                inputProps={{ 'aria-required': isEn }}
              />
            );
          })}
        </>
      )}
      {stepNavigation}
    </Box>
  );
}
